import type { Mutation } from '@tahi/core';
import { createStore, type Store } from '../ui/store';
import { ApiError, apiFetch, type Fetcher } from './api';
import { SERVER_DEVICE_ID } from './deviceId';
import type { LocalDb } from './localDb';
import { countOutbox, markOutboxFailed, readOutbox } from './outbox';
import { clearState, getState, setState } from './syncState';
import { applyPushResult, applyRemote, type AdjustedAt, type RemoteChange } from './writes';

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error' | 'signed_out';

export interface SyncStatus {
  state: SyncState;
  pending: number;
  lastSyncedAt: string | null;
  error: string | null;
  /** Changes the server refused for good since this client started ("Some changes couldn't be saved"). */
  rejected?: number;
}

export interface Rejection {
  id: string;
  reason: string;
}

export interface PushResponse {
  applied: string[];
  /** Every reason except `failed` is final; `failed` is retried (conventions §14). */
  rejected: Rejection[];
  /** Mutations whose `at` the server clamped, with the time it stored. */
  adjusted: AdjustedAt[];
}

export interface PullResponse {
  changes: RemoteChange[];
  cursor: number;
  more: boolean;
  /** The saved cursor is past the server's latest seq: clear it and pull again from 0. */
  reset?: boolean;
}

export interface SyncClient {
  status: Store<SyncStatus>;
  syncNow(): Promise<void>;
  /** Sync soon (debounced). Call after every local change. */
  schedule(delayMs?: number): void;
  start(): void;
  stop(): void;
}

export interface SyncDeps {
  db: LocalDb;
  userId: string;
  deviceId: string;
  fetcher?: Fetcher;
  isOnline?: () => boolean;
  onSignedOut?: () => void;
  pageSize?: number;
  /**
   * The newest local migration. An older app skips changes for tables and columns it doesn't
   * have yet but still moves its cursor past them, so when this changes the pull starts over
   * from 0. Pulling a change twice is a no-op (last-write-wins clocks), so nothing is doubled.
   */
  schema?: string;
}

const PERIODIC_MS = 60_000;

/** The Worker refuses pushes over 20 mutations (D1 free-plan query budget, conventions §14). */
export const MAX_PUSH = 20;
/** The Worker answers 413 too_large for a push body over 1 MB (its MAX_PUSH_BYTES). */
export const MAX_PUSH_BYTES = 1024 * 1024;

const utf8 = new TextEncoder();

export function backoffDelay(failures: number): number {
  return Math.min(60_000, 1000 * 2 ** Math.max(0, failures - 1));
}

export function createSyncClient(deps: SyncDeps): SyncClient {
  const { db, userId, deviceId } = deps;
  if (deviceId === SERVER_DEVICE_ID) {
    throw new Error(`Device id '${SERVER_DEVICE_ID}' is reserved for the server`);
  }
  const pageSize = Math.max(1, Math.min(deps.pageSize ?? MAX_PUSH, MAX_PUSH));
  const isOnline = deps.isOnline ?? (() => navigator.onLine);
  const status = createStore<SyncStatus>({
    state: 'idle',
    pending: 0,
    lastSyncedAt: null,
    error: null,
    rejected: 0,
  });
  const patch = (p: Partial<SyncStatus>) => status.set({ ...status.get(), ...p });

  let running: Promise<void> | null = null;
  let again = false;
  let failures = 0;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let interval: ReturnType<typeof setInterval> | undefined;

  const bodyFor = (mutations: Mutation[]) => ({ deviceId, mutations });
  const bodyBytes = (mutations: Mutation[]) =>
    utf8.encode(JSON.stringify(bodyFor(mutations))).length;

  /** Removes applied and finally refused mutations, and rewrites clamped clocks, in one transaction. */
  async function settle(
    sent: Mutation[],
    applied: string[],
    final: Rejection[],
    adjusted: AdjustedAt[],
  ): Promise<void> {
    await applyPushResult({ db, userId }, sent, [...applied, ...final.map((r) => r.id)], adjusted);
    if (final.length > 0) {
      console.warn('Tahi sync: the server refused some changes', final);
      await setState(db, 'last_rejected', JSON.stringify(final));
      patch({ rejected: (status.get().rejected ?? 0) + final.length });
    }
  }

  async function push(): Promise<void> {
    let size = pageSize;
    for (;;) {
      let batch = await readOutbox(db, size);
      if (batch.length === 0) return;
      while (batch.length > 1 && bodyBytes(batch) > MAX_PUSH_BYTES) {
        batch = batch.slice(0, Math.ceil(batch.length / 2));
      }

      let res: PushResponse;
      try {
        res = await apiFetch<PushResponse>('/api/sync/push', {
          json: bodyFor(batch),
          fetcher: deps.fetcher,
        });
      } catch (err) {
        if (err instanceof ApiError && err.status === 413) {
          // Body over 1 MB: try half as many. A single mutation that big can never be sent.
          if (batch.length > 1) size = Math.max(1, Math.floor(batch.length / 2));
          else await settle(batch, [], [{ id: batch[0].id, reason: 'too_large' }], []);
          continue;
        }
        if (
          err instanceof ApiError &&
          err.status >= 400 &&
          err.status < 500 &&
          err.status !== 401
        ) {
          // The whole push was refused: count it against these mutations so they can't block forever.
          const dropped = await markOutboxFailed(
            db,
            batch.map((m) => m.id),
            err.message,
          );
          if (dropped.length > 0) {
            await settle(
              batch,
              [],
              dropped.map((id) => ({ id, reason: err.code })),
              [],
            );
          }
        }
        // 5xx and network errors aren't the mutations' fault: back off, attempts unchanged.
        throw err;
      }

      const applied = res.applied ?? [];
      const rejected = res.rejected ?? [];
      const final = rejected.filter((r) => r.reason !== 'failed');
      const failedIds = rejected.filter((r) => r.reason === 'failed').map((r) => r.id);
      let blocked = false;
      if (failedIds.length > 0) {
        // The server hit an error on this one and stopped there; the rest stay queued in order.
        const dropped = await markOutboxFailed(db, failedIds, 'failed');
        final.push(...dropped.map((id) => ({ id, reason: 'failed' })));
        blocked = dropped.length < failedIds.length;
      }
      await settle(batch, applied, final, res.adjusted ?? []);
      if (blocked) throw new Error("The server couldn't save a change. Trying again soon.");
      if (applied.length + final.length === 0) {
        throw new Error('The server accepted none of the changes');
      }
    }
  }

  async function pull(): Promise<void> {
    if (deps.schema && (await getState(db, 'pulled_schema')) !== deps.schema) {
      await clearState(db, 'cursor');
      await setState(db, 'pulled_schema', deps.schema);
    }
    let cursor = Number((await getState(db, 'cursor')) ?? '0');
    let restarted = false;
    for (;;) {
      const res = await apiFetch<PullResponse>(
        `/api/sync/pull?cursor=${cursor}&deviceId=${encodeURIComponent(deviceId)}`,
        { fetcher: deps.fetcher },
      );
      if (res.reset) {
        if (restarted) throw new Error('The server asked to start the pull over twice');
        restarted = true;
        await clearState(db, 'cursor');
        cursor = 0;
        continue;
      }
      await applyRemote({ db, userId }, res.changes, res.cursor);
      cursor = res.cursor;
      if (!res.more) return;
    }
  }

  async function runOnce(): Promise<void> {
    if (!isOnline()) {
      patch({ state: 'offline', pending: await countOutbox(db) });
      return;
    }
    patch({ state: 'syncing', error: null });
    try {
      await push();
      await pull();
      failures = 0;
      patch({
        state: 'idle',
        pending: await countOutbox(db),
        lastSyncedAt: new Date().toISOString(),
        error: null,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        patch({ state: 'signed_out' });
        deps.onSignedOut?.();
        return;
      }
      failures++;
      patch({
        state: isOnline() ? 'error' : 'offline',
        pending: await countOutbox(db),
        error: err instanceof Error ? err.message : String(err),
      });
      schedule(backoffDelay(failures));
    }
  }

  function syncNow(): Promise<void> {
    if (stopped) return Promise.resolve();
    if (running) {
      again = true;
      return running;
    }
    running = (async () => {
      do {
        again = false;
        await runOnce();
      } while (again && !stopped);
    })().finally(() => {
      running = null;
    });
    return running;
  }

  function schedule(delayMs = 500): void {
    if (stopped) return;
    void countOutbox(db).then((pending) => patch({ pending }));
    clearTimeout(timer);
    timer = setTimeout(() => void syncNow(), delayMs);
  }

  const onOnline = () => void syncNow();
  const onOffline = () => patch({ state: 'offline' });
  const onVisibility = () => {
    if (document.visibilityState === 'visible') void syncNow();
  };

  return {
    status,
    syncNow,
    schedule,
    start() {
      stopped = false;
      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);
      document.addEventListener('visibilitychange', onVisibility);
      interval = setInterval(() => {
        if (document.visibilityState === 'visible') void syncNow();
      }, PERIODIC_MS);
      void syncNow();
    },
    stop() {
      stopped = true;
      clearTimeout(timer);
      clearInterval(interval);
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
        document.removeEventListener('visibilitychange', onVisibility);
      }
    },
  };
}
